import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./sessions.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import maleDefaultPic from "../../assets/male_default_pic.png";
import femaleDefaultPic from "../../assets/female_default_pic.jpeg";
import closeIcon from "../../assets/close_icon.png";

interface User {
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  gender: string;
  age: string;
  weight: string;
  height: string;
  goal: string;
  activity: string;
  medicalConcern: string;
  conditions: object;
  startingBmi: string;
  languageCode: string;
  profilePicture: string;
  activePlans: any;
  historyPlans: any;
  weightEntries: object;
  dietPlan: object;
  createdAt: Date;
  updatedAt: Date;
}

const Sessions = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true); // Add loading state
  const [expandedUsers, setExpandedUsers] = useState<{
    [key: string]: boolean;
  }>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  // Fetch user details and users
  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllUsers();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-users-info`
      );
      setUsers(response.data.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false); // Stop the loading indicator once API responds
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  // Expand or collapse individual user cards
  const toggleExpanded = (userId: string) => {
    setExpandedUsers((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  // Filter users based on phone number
  const filteredUsers = users.filter((user) =>
    user.phoneNumber.includes(searchTerm)
  );

  return (
    <div className="search-user-by-phone-container">
      {/* Loading indicator */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          {/* Search Box */}
          <div className="search-box">
            <h2>Sessions</h2>
            <input
              type="text"
              placeholder="Search by Phone Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="user-list">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div key={user._id} className="user-card">
                  <div className="user-details">
                    <p>
                      <strong>Name:</strong> <span>{user.name}</span>
                    </p>
                    <p>
                      <strong>Phone Number:</strong>{" "}
                      <span>{user.phoneNumber}</span>
                    </p>
                    <p>
                      <strong>Email:</strong> <span>{user.email}</span>
                    </p>
                    <p>
                      <strong>Gender:</strong> <span>{user.gender}</span>
                    </p>
                    <p>
                      <strong>Age:</strong> <span>{user.age}</span>
                    </p>
                    <p>
                      <strong>Height:</strong> <span>{user.height}</span>
                    </p>

                    {/* Show more details only if the card is expanded */}
                    {expandedUsers[user._id] && (
                      <>
                        <p>
                          <strong>Weight:</strong> <span>{user.weight}</span>
                        </p>
                        <p>
                          <strong>Goal:</strong> <span>{user.goal}</span>
                        </p>
                        <p>
                          <strong>Activity:</strong>{" "}
                          <span>{user.activity}</span>
                        </p>
                        <p>
                          <strong>BMI at the time of joining:</strong>{" "}
                          <span>{parseFloat(user.startingBmi).toFixed(2)}</span>
                        </p>
                        <p>
                          <strong>Medical Concern:</strong>{" "}
                          <span>{user.medicalConcern}</span>
                        </p>
                        <p>
                          <strong>Diabetes:</strong>{" "}
                          <span>
                            {user.conditions["diabetes"] ? "Yes" : "No"}
                          </span>
                        </p>
                        <p>
                          <strong>Menopause:</strong>{" "}
                          <span>
                            {user.conditions["menopause"] ? "Yes" : "No"}
                          </span>
                        </p>
                        <p>
                          <strong>PCOS/PCOD:</strong>{" "}
                          <span>
                            {user.conditions["pcosPcod"] ? "Yes" : "No"}
                          </span>
                        </p>
                        <p>
                          <strong>Thyroidism:</strong>{" "}
                          <span>
                            {user.conditions["thyroidism"] ? "Yes" : "No"}
                          </span>
                        </p>
                        <p>
                          <strong>Cardiovascular:</strong>{" "}
                          <span>
                            {user.conditions["cardiovascular"] ? "Yes" : "No"}
                          </span>
                        </p>
                        <p>
                          <strong>Other Medical Conditions:</strong>{" "}
                          <span>
                            {user.conditions["otherMedicalConditions"]
                              ? "Yes"
                              : "No"}
                          </span>
                        </p>
                        <p
                          style={{
                            display: "flex",
                            flexDirection: user.activePlans[0]?.planName
                              ? "column"
                              : "row",
                            alignItems: "baseline",
                            gap: "5px",
                          }}
                        >
                          <strong>Active Plan:</strong>{" "}
                          {user.activePlans[0]?.planName ? (
                            <div className="history-plans-card">
                              {user.activePlans.map(
                                (plan: any, index: number) => (
                                  <span
                                    className="history-plans-card-item"
                                    key={index}
                                  >
                                    <span style={{ fontWeight: "500" }}>
                                      {index + 1 + ". " + plan.planName + ":"}{" "}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Price: ₹" + plan.price}{" "}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Start date: " +
                                        new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.startDate))}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • End date: " +
                                        new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.endDate))}
                                    </span>
                                  </span>
                                )
                              )}
                            </div>
                          ) : (
                            <span style={{ color: "red" }}>No Active plan</span>
                          )}
                        </p>
                        <p
                          style={{
                            display: "flex",
                            flexDirection: user.historyPlans[0]?.planName
                              ? "column"
                              : "row",
                            alignItems: "baseline",
                            gap: "5px",
                          }}
                        >
                          <strong>History Plans:</strong>
                          {user.historyPlans[0]?.planName ? (
                            <div className="history-plans-card">
                              {user.historyPlans.map(
                                (plan: any, index: number) => (
                                  <span
                                    className="history-plans-card-item"
                                    key={index}
                                  >
                                    <span style={{ fontWeight: "500" }}>
                                      {index + 1 + ". " + plan.planName + ":"}{" "}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Price: ₹" + plan.price}{" "}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Start date: " +
                                        new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.startDate))}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Expired on: " +
                                        new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.endDate))}
                                    </span>
                                  </span>
                                )
                              )}
                            </div>
                          ) : (
                            <span style={{ color: "red" }}>
                              No History plan
                            </span>
                          )}
                        </p>
                      </>
                    )}
                    <button
                      className="toggle-details-button"
                      onClick={() => toggleExpanded(user._id)}
                    >
                      {expandedUsers[user._id] ? "View Less" : "View More"}
                    </button>
                  </div>
                  <span className="user-card-profile">
                    {user?.profilePicture != null ? (
                      <img
                        className="user-card-profile-pic"
                        src={user?.profilePicture}
                      ></img>
                    ) : user.gender == "Male" ? (
                      <img
                        className="user-card-profile-pic"
                        src={maleDefaultPic}
                      ></img>
                    ) : (
                      <img
                        className="user-card-profile-pic"
                        src={femaleDefaultPic}
                      ></img>
                    )}
                    {user.activePlans[0]?.planName != null ? (
                      <span className="user-card-profile-tag">
                        Subscribed user
                      </span>
                    ) : (
                      ""
                    )}
                  </span>
                </div>
              ))
            ) : (
              <p>No User found.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Sessions;
